/**
 * ChartCard Component
 * Individual configurable chart with field selector, chart type, and visualization
 */

import React, { useRef, useState, useMemo } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import { getAnswerValue } from '../../../../utils/analyticsUtils';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { ChartableField } from '../../../../utils/chartableFields';
import { AggregatedData } from '../../../../utils/analyticsUtils';
import toast from 'react-hot-toast';
import { useCanActOnOutreach } from '../../outreachAccess';

interface ChartConfig {
  id: string;
  question_id: string;
  chart_type: string;
  title?: string;
  chart_config?: any;
}

interface ChartCardProps {
  chart: ChartConfig;
  chartableFields: ChartableField[];
  data: AggregatedData[];
  responses?: any[];
  onUpdate: (updates: Partial<ChartConfig>) => void;
  onDelete: () => void;
  hideDownload?: boolean;
  /** Optional drag handle rendered before the title (for grid reordering) */
  dragHandle?: React.ReactNode;
}

const COLORS = ['#8b5cf6', '#6366f1', '#3b82f6', '#14b8a6', '#22c55e', '#eab308', '#f97316', '#ef4444'];

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'as', 'is', 'was', 'are', 'were', 'been',
  'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'must', 'shall', 'can', 'need', 'dare', 'ought',
  'used', 'i', 'me', 'my', 'myself', 'we', 'our', 'ours', 'ourselves', 'you',
  'your', 'yours', 'yourself', 'yourselves', 'he', 'him', 'his', 'himself',
  'she', 'her', 'hers', 'herself', 'it', 'its', 'itself', 'they', 'them',
  'their', 'theirs', 'themselves', 'what', 'which', 'who', 'whom', 'this',
  'that', 'these', 'those', 'am', 'isn', 'aren', 'wasn', 'weren', 'hasn',
  'haven', 'hadn', 'doesn', 'don', 'didn', 'won', 'wouldn', 'couldn',
  'shouldn', 'mightn', 'mustn', 'just', 'very', 'really', 'also', 'so',
  'than', 'too', 'only', 'same', 'into', 'some', 'such', 'no', 'not', 'more',
  'most', 'other', 'any', 'all', 'each', 'every', 'both', 'few', 'many'
]);

const ChartCard: React.FC<ChartCardProps> = ({
  chart,
  chartableFields,
  data,
  responses = [],
  onUpdate,
  onDelete,
  hideDownload = false,
  dragHandle
}) => {
  // Read-only viewers see the chart, not its configuration (org admins only).
  const canAct = useCanActOnOutreach();
  const chartRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const selectedField = chartableFields.find(f => f.id === chart.question_id);

  const wordCloudData = useMemo(() => {
    if (chart.chart_type !== 'wordcloud' || !chart.question_id || !responses.length) {
      return [];
    }

    const wordCounts: Record<string, number> = {};

    responses.forEach(response => {
      // Get answer value from both nested and flat structures
      const text = getAnswerValue(response, chart.question_id);
      
      if (typeof text === 'string' && text.trim()) {
        const words = text.toLowerCase()
          .replace(/[^\w\s]/g, ' ')
          .split(/\s+/)
          .filter(word => word.length > 2 && !STOP_WORDS.has(word));
        
        words.forEach(word => {
          wordCounts[word] = (wordCounts[word] || 0) + 1;
        });
      }
    });

    return Object.entries(wordCounts)
      .map(([word, count]) => ({ word, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }, [chart.chart_type, chart.question_id, responses]);
  
  const handleDownload = async () => {
    if (!chartRef.current) return;
    
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas' as any)).default;
      
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      const link = document.createElement('a');
      link.download = `chart-${chart.title || selectedField?.label || 'export'}.png`.replace(/\s+/g, '-').toLowerCase();
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Chart downloaded');
    } catch (error) {
      console.error('Failed to download chart:', error);
      toast.error('Failed to download chart');
    } finally {
      setDownloading(false);
    }
  };

  const renderChart = () => {
    if (!data || data.length === 0) {
      return (
        <div className="h-[250px] flex items-center justify-center text-text-tertiary dark:text-text-dark-tertiary">
          No data available for this field
        </div>
      );
    }

    const chartData = data.slice(0, 10);

    switch (chart.chart_type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <XAxis 
                dataKey="value" 
                tick={{ fontSize: 12 }} 
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis allowDecimals={false} />
              <Tooltip 
                formatter={(value: number) => [value, 'Count']}
                contentStyle={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'horizontal-bar':
        return (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" allowDecimals={false} />
              <YAxis 
                dataKey="value" 
                type="category" 
                tick={{ fontSize: 12 }} 
                width={120}
              />
              <Tooltip 
                formatter={(value: number) => [value, 'Count']}
                contentStyle={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
      case 'donut':
        return (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="count"
                nameKey="value"
                cx="50%"
                cy="50%"
                innerRadius={chart.chart_type === 'donut' ? 50 : 0}
                outerRadius={80}
                label={({ percentage }) => `${percentage}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={entry.value} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => [value, 'Count']}
                contentStyle={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px'
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <XAxis 
                dataKey="value" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis allowDecimals={false} />
              <Tooltip 
                formatter={(value: number) => [value, 'Count']}
                contentStyle={{
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={{ fill: '#8b5cf6', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'wordcloud': {
        if (wordCloudData.length === 0) {
          return (
            <div className="h-[250px] flex items-center justify-center text-text-tertiary dark:text-text-dark-tertiary">
              No text data available for word cloud. Select a text/comment field.
            </div>
          );
        }
        
        const maxCount = Math.max(...wordCloudData.map(w => w.count));
        const minSize = 12;
        const maxSize = 48;
        
        return (
          <div className="h-[250px] overflow-hidden flex flex-wrap items-center justify-center gap-2 p-4">
            {wordCloudData.map((item, index) => {
              const size = minSize + ((item.count / maxCount) * (maxSize - minSize));
              const colorIndex = index % COLORS.length;
              return (
                <span
                  key={item.word}
                  className="inline-block cursor-default transition-transform hover:scale-110"
                  style={{
                    fontSize: `${size}px`,
                    color: COLORS[colorIndex],
                    fontWeight: size > 24 ? 600 : 400,
                    opacity: 0.7 + (item.count / maxCount) * 0.3
                  }}
                  title={`${item.word}: ${item.count} occurrences`}
                >
                  {item.word}
                </span>
              );
            })}
          </div>
        );
      }

      default:
        return <div className="text-text-tertiary">Unsupported chart type</div>;
    }
  };

  return (
    <div className="card" data-chart-id={chart.id} ref={chartRef}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-1">
          {dragHandle}
          {canAct ? (
            <input
              type="text"
              value={chart.title || selectedField?.label || ''}
              onChange={(e) => onUpdate({ title: e.target.value })}
              className="flex-1 text-lg font-semibold bg-transparent border-none p-0 focus:ring-0 text-text-primary dark:text-text-dark-primary"
              placeholder="Chart title"
            />
          ) : (
            <h4 className="flex-1 text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {chart.title || selectedField?.label || ''}
            </h4>
          )}
        </div>
        <div className="flex items-center gap-1" data-export-hide>
          {!hideDownload && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="p-1 text-text-tertiary hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
              aria-label="Download chart as PNG"
              title="Download as PNG"
            >
              {downloading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
            </button>
          )}
          {canAct && (
            <button
              onClick={onDelete}
              className="p-1 text-text-tertiary hover:text-error-500 dark:hover:text-error-400 transition-colors"
              aria-label="Remove chart"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {canAct && (
        <div className="flex gap-4 mb-4" data-export-hide>
          <div className="flex-1">
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Field
            </label>
            <select
              value={chart.question_id}
              onChange={(e) => {
                const newFieldId = e.target.value;
                const newField = chartableFields.find(f => f.id === newFieldId);
                const updates: Partial<ChartConfig> = { 
                  question_id: newFieldId,
                  title: newField?.label || ''
                };
                if (newField && !newField.suggestedChartTypes.includes(chart.chart_type as any)) {
                  updates.chart_type = newField.suggestedChartTypes[0] || 'bar';
                }
                onUpdate(updates);
              }}
              className="input w-full"
            >
              <option value="">Select field...</option>
              {chartableFields.map(field => (
                <option key={field.id} value={field.id}>{field.label}</option>
              ))}
            </select>
          </div>

          <div className="w-40">
            <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
              Chart Type
            </label>
            <select
              value={chart.chart_type}
              onChange={(e) => onUpdate({ chart_type: e.target.value })}
              className="input w-full"
            >
              {(selectedField?.suggestedChartTypes ?? ['bar', 'horizontal-bar', 'pie', 'donut', 'line', 'wordcloud']).map(type => (
                <option key={type} value={type}>
                  {type === 'bar' ? 'Bar' :
                   type === 'horizontal-bar' ? 'Horizontal Bar' :
                   type === 'pie' ? 'Pie' :
                   type === 'donut' ? 'Donut' :
                   type === 'line' ? 'Line' :
                   type === 'wordcloud' ? 'Word Cloud' : type}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {chart.question_id ? (
        renderChart()
      ) : (
        <div className="h-[250px] flex items-center justify-center text-text-tertiary dark:text-text-dark-tertiary">
          Select a field to display chart
        </div>
      )}
    </div>
  );
};

export default ChartCard;
